export const saveCalInvite = (auction, auctionUser) => {
  // Create the .ics URL

  const supportEmail = auction.SupportEmail
  const attendeeName = `${auctionUser?.FirstName ? auctionUser?.FirstName : ''} ${auctionUser?.LastName ? auctionUser?.LastName : ''}`
  const attendeeEmail = `${auctionUser?.ContactEmail ? auctionUser?.ContactEmail : ''}`

  // ORGANIZER and ATTENDEE must differ for METHOD:REQUEST to work in Outlook
  // if they match, Outlook drops the event after import

  // METHOD:REQUEST enables Reply/Forward in Outlook but requires ORGANIZER != ATTENDEE email
  // METHOD:PUBLISH is safer for "add to calendar" flows — event persists without RSVP logic
  // METHOD:PUBLISH does not require ATTENDEE, but keeping it for clients that use it for display
  //needs action requires them to RSVP
  // `ATTENDEE;CN=${escapeICS(attendeeName.trim())};RSVP=TRUE;PARTSTAT=NEEDS-ACTION;ROLE=REQ-PARTICIPANT:mailto:${attendeeEmail}`,
  // https://stackoverflow.com/questions/35645402/how-to-specify-timezone-in-ics-file-which-will-work-efficiently-with-google-outl

  let url = [
    "BEGIN:VCALENDAR",
    "METHOD:REQUEST",
    "VERSION:2.0",
    "PRODID:-//UCLA Giving//Auction Calendar//EN",
    "BEGIN:VEVENT",
    `DTSTAMP:${formatDate(new Date())}`,
    `DTSTART:${formatDate(auction.StartTime)}`,
    `DTEND:${formatDate(auction.EndTime)}`,
    `LOCATION:${auction.AuctionLocation}`,
    `SUMMARY:${auction.Title}`,
    `UID:${uuidv4()}`,
    ...(supportEmail !== attendeeEmail ? [`ORGANIZER;CN=${escapeICS(supportEmail)}:mailto:${supportEmail}`] : []),
    `ATTENDEE;CN=${escapeICS(attendeeName.trim())};RSVP=TRUE;PARTSTAT=ACCEPTED;ROLE=REQ-PARTICIPANT:mailto:${attendeeEmail}`,
    "SEQUENCE:0",
    "STATUS:CONFIRMED",
    "END:VEVENT",
    "END:VCALENDAR"
  ].join("\r\n")

  let blob = new Blob([url], { type: 'text/calendar;charset=utf-8' })

  if (/msie\s|trident\/|edge\//i.test(window.navigator.userAgent)) {
    // Open/Save link in IE and Edge
    window.navigator.msSaveBlob(blob, `event.ics`)
  } else {
    // Open/Save link in Modern Browsers
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `${auction.Title}.ics`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(link.href)
  }
}

function escapeICS(text) {
  return text ?
  text.replace(/\\/g, "\\\\")
      .replace(/\n/g, "\\n")
      .replace(/,/g, "\\,")
      .replace(/;/g, "\\;")
  : text
}

const formatDate = (isoAuctionDate) => {
  let eventDate = ''
  let counter = 0

  while (eventDate.length < 15) {
    if (isoAuctionDate[counter] !== ':' && isoAuctionDate[counter] !== '-' && isoAuctionDate[counter] !== '.') {
      eventDate += isoAuctionDate[counter]
    }
    counter++
  }
  return eventDate
}

function uuidv4() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'
  .replace(/[xy]/g, function (c) {
      const r = Math.random() * 16 | 0, 
          v = c == 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
  });
}

export const getItemUserStatus = (auction, item, currentUserId) => {

  const itemBids = auction.Bids.filter(bid => bid.ItemId === item.ItemId && bid.UserId === currentUserId)

  const isHighBidder = item.CurrentHighBidder === currentUserId

  if (isHighBidder) return 'winning'
  if (itemBids.length > 0) return 'outbid'
  else return undefined
  
}

export const AUCTION_ITEM = 'AUCTION_ITEM'
export const FIXED_PRICE_ITEM = 'FIXED_PRICE_ITEM'

export const getOrderEntryKey = ({ ItemType, ItemId }) => `${ItemType}:${ItemId}`

export const getDefaultItemDisplayOrder = (auction) => [
  ...(auction?.Items || []).map((item) => ({
    ItemType: AUCTION_ITEM,
    ItemId: item.ItemId,
  })),
  ...(auction?.FixedItems || []).map((item) => ({
    ItemType: FIXED_PRICE_ITEM,
    ItemId: item.FixedItemId,
  })),
]

export const getInitialItemDisplayOrder = (auction) => {
  const defaultOrder = getDefaultItemDisplayOrder(auction)
  const savedOrder = auction?.ItemDisplayOrder

  if (!Array.isArray(savedOrder) || savedOrder.length === 0) return defaultOrder

  const availableEntries = new Set(defaultOrder.map(getOrderEntryKey))
  const includedEntries = new Set()
  const reconciledOrder = []

  savedOrder.forEach(({ ItemType, ItemId }) => {
    const orderEntry = { ItemType, ItemId }
    const entryKey = getOrderEntryKey(orderEntry)

    if (availableEntries.has(entryKey) && !includedEntries.has(entryKey)) {
      reconciledOrder.push(orderEntry)
      includedEntries.add(entryKey)
    }
  })

  defaultOrder.forEach((orderEntry) => {
    const entryKey = getOrderEntryKey(orderEntry)

    if (!includedEntries.has(entryKey)) reconciledOrder.push(orderEntry)
  })

  return reconciledOrder
}

export const getItemsInDisplayOrder = ({ auction, items, fixedItems } = {}) => {
  if (!auction) return []

  const displayItems = items ?? auction.Items ?? []
  const displayFixedItems = fixedItems ?? auction.FixedItems ?? []
  const itemLookup = new Map()

  displayItems.forEach((item) => {
    itemLookup.set(getOrderEntryKey({
      ItemType: AUCTION_ITEM,
      ItemId: item.ItemId,
    }), item)
  })

  displayFixedItems.forEach((item) => {
    itemLookup.set(getOrderEntryKey({
      ItemType: FIXED_PRICE_ITEM,
      ItemId: item.FixedItemId,
    }), item)
  })

  const displayAuction = {
    ...auction,
    Items: displayItems,
    FixedItems: displayFixedItems,
  }

  return getInitialItemDisplayOrder(displayAuction)
    .map((orderEntry) => itemLookup.get(getOrderEntryKey(orderEntry)))
    .filter(Boolean)
}

export const handleEscapeKey = (event, handleFunction) => {
  if (event.key === 'Escape') {
    handleFunction()
  }
}

export const handleEnterKey = (event, handleFunction) => {
  if (event.key === 'Enter') {
    handleFunction()
  }
}

// set session storage item and trigger custom event for unsaved auction check
export function setSessionItem(key, value) {
  sessionStorage.setItem(key, JSON.stringify(value))

  window.dispatchEvent(new CustomEvent('session-storage-change', {
    detail: { key, value },
  }))
}
